import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';

const Container = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Content = styled.div`
  text-align: center;
  max-width: 600px;
`;

const ErrorCode = styled.h1`
  font-size: 8rem;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.secondary}
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 1rem;
  line-height: 1;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  margin-bottom: 2rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.darkGray};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
`;

const NotFoundPage: React.FC = () => {
  const pageTitle = 'Page Not Found (404) | ElectroCable';
  const pageDescription = 'Sorry, the page you are looking for cannot be found. It might have been removed, renamed, or is temporarily unavailable. Please return to the homepage or contact us.';
  const siteUrl = 'https://www.yourwebsite.com';
  const currentPageUrl = `${siteUrl}/404`;
  const ogImageUrl = `${siteUrl}/images/logo.svg`;

  return (
    <Container>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentPageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={currentPageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Content>
        <ErrorCode>404</ErrorCode>
        <Title>Page Not Found</Title>
        <Description>
          The page you are looking for might have been removed, had its name changed,
          or is temporarily unavailable.
        </Description>
        <ButtonContainer>
          <Button as={Link} to="/" variant="primary">
            Back to Home
          </Button>
          <Button as={Link} to="/contact" variant="outlined">
            Contact Us
          </Button>
        </ButtonContainer>
      </Content>
    </Container>
  );
};

export default NotFoundPage; 